/**
 * AcademyPage - Main Academy view
 * 3 modules, 12 lessons, progress tracking
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
  GraduationCap, CheckCircle, Circle, ChevronRight, 
  Sparkles, TrendingUp, MessageSquare, Loader2,
  Play, Lock, Trophy, Target, Zap, Share2, Image, FileText,
  Heart, QrCode, Facebook, Award, BarChart3
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import PanelLayout from '@/components/PanelLayout';

import { API } from '@/utils/api';
import { useTranslation } from 'react-i18next';

// Lesson icons mapping
const lessonIcons = {
  'M1_L1': Sparkles,    // AI Scan
  'M1_L2': MessageSquare, // Contact
  'M1_L3': Target,      // Add offer
  'M1_L4': Share2,      // Share link
  'M2_L1': Image,       // Photos
  'M2_L2': FileText,    // Description
  'M2_L3': Heart,       // Health tests
  'M2_L4': Award,       // Titles
  'M3_L1': QrCode,      // QR
  'M3_L2': Facebook,    // FB post
  'M3_L3': FileText,    // Pedigree
  'M3_L4': BarChart3,   // KPI
};

// Module colors
const moduleColors = {
  'M1': { bg: 'bg-blue-50', border: 'border-blue-200', accent: 'text-blue-600', progress: 'bg-blue-500' },
  'M2': { bg: 'bg-purple-50', border: 'border-purple-200', accent: 'text-purple-600', progress: 'bg-purple-500' },
  'M3': { bg: 'bg-green-50', border: 'border-green-200', accent: 'text-green-600', progress: 'bg-green-500' },
};

// Lesson Card Component
function LessonCard({ lesson, moduleId, onNavigate }) {
  const Icon = lessonIcons[lesson.id] || Circle;
  const colors = moduleColors[moduleId];
  const isDone = lesson.done;

  return (
    <div 
      className={`flex items-center gap-3 p-3 rounded-xl transition-all cursor-pointer
        ${isDone ? 'bg-green-50 border border-green-200' : 'bg-white border border-gray-100 hover:border-zinc-200 hover:shadow-sm'}`}
      onClick={() => !isDone && onNavigate(lesson.cta_href, lesson.id)}
    >
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0
        ${isDone ? 'bg-green-100' : colors.bg}`}>
        {isDone ? (
          <CheckCircle className="w-5 h-5 text-green-600" />
        ) : (
          <Icon className={`w-5 h-5 ${colors.accent}`} />
        )}
      </div>
      
      <div className="flex-1 min-w-0">
        <p className={`font-medium truncate ${isDone ? 'text-green-700' : 'text-zinc-900'}`}>
          {lesson.title}
        </p>
      </div>
      
      {!isDone && (
        <ChevronRight className="w-5 h-5 text-zinc-400 flex-shrink-0" />
      )}
    </div>
  );
}

// Module Card Component
function ModuleCard({ module, onNavigate }) {
  const colors = moduleColors[module.id];
  const completedLessons = module.lessons.filter(l => l.done).length;
  const totalLessons = module.lessons.length;
  const progress = totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0;
  const isComplete = progress === 100;

  // Find next incomplete lesson
  const nextLesson = module.lessons.find(l => !l.done);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-2xl border-2 overflow-hidden ${isComplete ? 'border-green-300 bg-green-50/50' : `${colors.border} ${colors.bg}`}`}
    >
      {/* Header */}
      <div className="p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              {isComplete && <Trophy className="w-5 h-5 text-green-500" />}
              <h3 className="font-bold text-zinc-900 text-base sm:text-lg">{module.title}</h3>
            </div>
            <p className="text-zinc-600 text-sm">{module.description}</p>
          </div>
          <div className={`px-3 py-1 rounded-full text-sm font-semibold ${isComplete ? 'bg-green-100 text-green-700' : `${colors.bg} ${colors.accent}`}`}>
            {progress}%
          </div>
        </div>

        {/* Progress bar */}
        <div className="h-2 bg-zinc-200 rounded-full overflow-hidden mb-4">
          <div 
            className={`h-full rounded-full transition-all duration-500 ${isComplete ? 'bg-green-500' : colors.progress}`}
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Lessons list */}
        <div className="space-y-2">
          {module.lessons.map(lesson => (
            <LessonCard 
              key={lesson.id} 
              lesson={lesson} 
              moduleId={module.id}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      </div>

      {/* Footer CTA */}
      {!isComplete && nextLesson && (
        <div className="px-4 sm:px-5 pb-4 sm:pb-5">
          <Button
            onClick={() => onNavigate(nextLesson.cta_href, nextLesson.id)}
            className="w-full bg-[#F47A3B] hover:bg-[#E06A2B] text-white"
          >
            <Play className="w-4 h-4 mr-2" />
            {nextLesson.cta_label}
          </Button>
        </div>
      )}

      {isComplete && (
        <div className="px-4 sm:px-5 pb-4 sm:pb-5">
          <div className="flex items-center justify-center gap-2 p-3 bg-green-100 rounded-xl">
            <Trophy className="w-5 h-5 text-green-600" />
            <span className="font-medium text-green-700">Moduł ukończony!</span>
          </div>
        </div>
      )}
    </motion.div>
  );
}

export default function AcademyPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [stats, setStats] = useState({ aiScore: 0, leads7d: 0 });
  const [userState, setUserState] = useState(null);
  const [redirecting, setRedirecting] = useState(false);
  const [redirectMessage, setRedirectMessage] = useState('');

  useEffect(() => {
    fetchAcademy();
  }, []);

  const fetchAcademy = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [academyRes, statsRes, stateRes] = await Promise.all([
        axios.get(`${API}/academy`, { headers }),
        axios.get(`${API}/user/stats`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API}/user/academy-state`, { headers }).catch(() => ({ data: {} }))
      ]);
      
      setData(academyRes.data);
      setStats({
        aiScore: statsRes.data?.ai_score || 0,
        leads7d: statsRes.data?.leads_7d || 0
      });
      setUserState(stateRes.data || {});
    } catch (e) {
      console.error('Error fetching academy:', e);
    } finally {
      setLoading(false);
    }
  };

  // Smart redirect logic based on user state
  const resolveNextAction = (lessonId, originalHref) => {
    if (!userState) return originalHref;

    const s = userState;

    // Blocker: no kennel = always redirect to kennel wizard
    if (!s.has_kennel) {
      return '/panel/profil';
    }

    // Map lesson IDs to actions
    switch (lessonId) {
      case 'M1_L1': // AI Scan
        if (!s.has_litter) return '/wizard/miot';
        return `/panel/ai-scan?target=litter&id=${encodeURIComponent(s.active_litter_id || '')}&from=academy`;
      
      case 'M1_L2': // Contact
        return '/panel/ustawienia';
      
      case 'M1_L3': // Add offer (miot)
        if (!s.has_litter) return '/wizard/miot';
        return '/panel/profil';
      
      case 'M1_L4': // Share link
        return '/panel/share';
      
      case 'M2_L1': // Add photos
        if (s.has_litter && s.active_litter_id) {
          return `/wizard/miot/${s.active_litter_id}?step=photos`;
        }
        return '/panel';
      
      case 'M2_L2': // Add description
        if (s.has_litter && s.active_litter_id) {
          return `/wizard/miot/${s.active_litter_id}?step=description`;
        }
        return '/panel';
      
      case 'M2_L3': // Health tests
        return '/panel';
      
      case 'M2_L4': // Titles
        return '/panel';
      
      case 'M3_L1': // QR
        return '/panel/share#qr';
      
      case 'M3_L2': // FB post
        return '/panel/share#facebook';
      
      case 'M3_L3': // Pedigree
        return '/panel';
      
      case 'M3_L4': // KPI
        return '/panel';
      
      default:
        return originalHref;
    }
  };

  const handleNavigate = async (href, lessonId) => {
    // Show loader
    setRedirecting(true);
    setRedirectMessage('Przygotowujemy najlepszy krok dla Ciebie…');

    // Track event
    axios.post(`${API}/academy/track`, { 
      event: 'academy_cta_click', 
      href,
      lesson_id: lessonId
    }, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    }).catch(() => {});

    // Resolve smart redirect
    const targetHref = resolveNextAction(lessonId, href);

    // Small delay for UX
    await new Promise(r => setTimeout(r, 800));

    setRedirecting(false);
    navigate(targetHref);
  };

  if (loading) {
    return (
      <PanelLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 text-[#F47A3B] animate-spin" />
        </div>
      </PanelLayout>
    );
  }

  // Redirect loader overlay
  if (redirecting) {
    return (
      <PanelLayout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
          <Loader2 className="w-10 h-10 text-[#F47A3B] animate-spin" />
          <p className="text-zinc-600 text-lg animate-pulse">{redirectMessage}</p>
        </div>
      </PanelLayout>
    );
  }

  const globalProgress = data?.global_progress || 0;
  const modules = data?.modules || [];

  return (
    <PanelLayout>
      <div className="max-w-4xl mx-auto space-y-6 sm:space-y-8">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-[#F47A3B] to-[#E06A2B] rounded-2xl p-5 sm:p-8 text-white"
        >
          <div className="flex items-start gap-4 mb-4">
            <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-white/20 flex items-center justify-center flex-shrink-0">
              <GraduationCap className="w-7 h-7 sm:w-8 sm:h-8" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold mb-1">Zwiększ liczbę zapytań o szczeniaki</h1>
              <p className="text-white/80 text-sm sm:text-base">Krótkie kroki → większa widoczność w AI</p>
            </div>
          </div>

          {/* Global Progress */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white/80 text-sm">Twój postęp</span>
              <span className="font-bold text-lg">{globalProgress}%</span>
            </div>
            <div className="h-3 bg-white/20 rounded-full overflow-hidden">
              <div 
                className="h-full bg-white rounded-full transition-all duration-700"
                style={{ width: `${globalProgress}%` }}
              />
            </div>
          </div>

          {/* Mini KPIs */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white/10 rounded-xl p-3">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                <span className="text-white/80 text-sm">AI Score</span>
              </div>
              <p className="text-2xl font-bold mt-1">{stats.aiScore}%</p>
            </div>
            <div className="bg-white/10 rounded-xl p-3">
              <div className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                <span className="text-white/80 text-sm">Leady (7 dni)</span>
              </div>
              <p className="text-2xl font-bold mt-1">{stats.leads7d}</p>
            </div>
          </div>
        </motion.div>

        {/* Modules */}
        <div className="space-y-4 sm:space-y-6">
          {modules.map((module, idx) => (
            <motion.div
              key={module.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
            >
              <ModuleCard module={module} onNavigate={handleNavigate} />
            </motion.div>
          ))}
        </div>

        {/* Completion Message */}
        {globalProgress === 100 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl p-6 text-white text-center"
          >
            <Trophy className="w-12 h-12 mx-auto mb-3" />
            <h2 className="text-xl font-bold mb-2">Gratulacje! 🎉</h2>
            <p className="text-white/90">Ukończyłeś wszystkie moduły Akademii. Twój profil jest zoptymalizowany pod widoczność w AI!</p>
          </motion.div>
        )}
      </div>
    </PanelLayout>
  );
}
